import { useEffect } from 'react';
import { Box, Container, Grid, Modal } from '@mui/material';
import { Controller, FieldErrors, FieldValues, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';

import CustomButton from '../../../components/Button/button';
import CustomTextField from '../../../components/TextField/textField';
import CustomTypography from '../../../components/Typography/typography';
import styles from '../../../assets/styles/editUser';
import {
  addUserByAdmin,
  adminSliceActions,
  updateuser,
} from '../../../redux/slice/adminSlice';

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
};

const EditUser = (props: any) => {
  const { open, handlePopUp, user, page, rowsPerPage } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const classes = styles();
  const updateUserData = useSelector(
    (state: any) => state.admin.updateuserData
  );
  const userData = useSelector((state: any) => state.admin.getuserData);

  const form = useForm({
    defaultValues: initialState,
  });
  const { handleSubmit, control, formState, reset, clearErrors } = form;
  const { errors } = formState;

  //submit method that calls edit user details api
  const onSubmit = (data: FieldValues) => {
    const newData = { ...data, email: data.email.toLowerCase() };

    user === 'Add'
      ? dispatch(addUserByAdmin({ newData, navigate, rowsPerPage, page: 0 }))
      : dispatch(updateuser({ newData, navigate, rowsPerPage, page }));
  };
  const onError = (errors: FieldErrors) => {};

  //function for when modal is closed
  const handleCloseButton = () => {
    dispatch(adminSliceActions.emptyState());
    reset(initialState);
    handlePopUp();
  };

  useEffect(() => {
    if (user !== 'Add' && open === true)
      user === null ? reset(initialState) : reset(user);
    updateUserData?._id && handleCloseButton();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, updateUserData, user]);

  return (
    <Container maxWidth={'xs'}>
      <Modal
        open={open}
        onClose={handleCloseButton}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box component='main' className={classes.boxClass}>
          <CustomTypography
            id='modal-modal-title'
            align='left'
            variant='h5'
            component='h2'
            className={classes.HeadingClass}
          >
            {user === 'Add' ? 'Add User' : 'Edit User'}
          </CustomTypography>
          <Box>
            <form onSubmit={handleSubmit(onSubmit, onError)}>
              <Grid container className={classes.grid} spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name='firstName'
                    control={control}
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        label='First Name'
                        size='medium'
                        fullWidth
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e.target.value, clearErrors('root'));
                        }}
                      />
                    )}
                    rules={{
                      required: {
                        value: true,
                        message: 'Please enter a first name',
                      },
                      validate: {
                        whitespace: (fieldValue: string) => {
                          return (
                            /^[ \t]+/.test(fieldValue) !== true ||
                            "firstName can't be whitespace"
                          );
                        },
                      },
                    }}
                  />
                  <CustomTypography
                    variant='body2'
                    color={'red'}
                    align='justify'
                  >
                    {errors.firstName?.message?.toString()}
                  </CustomTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name='lastName'
                    control={control}
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        label='Last Name'
                        fullWidth
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e.target.value, clearErrors('root'));
                        }}
                      />
                    )}
                    rules={{
                      required: {
                        value: true,
                        message: 'Please enter a last name',
                      },
                      validate: {
                        whitespace: (fieldValue: string) => {
                          return (
                            /^[ \t]+/.test(fieldValue) !== true ||
                            "last name can't be whitespace"
                          );
                        },
                      },
                    }}
                  />
                  <CustomTypography
                    variant='body2'
                    color={'red'}
                    align='justify'
                  >
                    {errors.lastName?.message?.toString()}
                  </CustomTypography>
                </Grid>

                <Grid item xs={12} sm={user === 'Add' ? 6 : 12}>
                  <Controller
                    name='email'
                    control={control}
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        label='Email'
                        autoComplete='new-email'
                        fullWidth
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e.target.value, clearErrors('root'));
                        }}
                      />
                    )}
                    rules={{
                      required: {
                        value: true,
                        message: 'Please enter a email',
                      },
                      pattern: {
                        value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g,
                        message: 'Please enter valid Email',
                      },
                      validate: {
                        whitespace: (fieldValue: string) => {
                          return (
                            /^[ \t]+/.test(fieldValue) !== true ||
                            "email can't be whitespace"
                          );
                        },
                      },
                    }}
                  />
                  <CustomTypography
                    variant='body2'
                    color={'red'}
                    align='justify'
                  >
                    {errors.email?.message?.toString()}
                  </CustomTypography>
                </Grid>
                {user === 'Add' && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name='password'
                      control={control}
                      render={({ field }) => (
                        <CustomTextField
                          {...field}
                          label='Password'
                          type='password'
                          fullWidth
                          autoComplete='new-password'
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            field.onChange(e.target.value, clearErrors('root'));
                          }}
                        />
                      )}
                      rules={{
                        required: {
                          value: true,
                          message: 'Please enter a password',
                        },
                        minLength: {
                          value: 6,
                          message: 'Password must be 6 character',
                        },
                        maxLength: {
                          value: 20,
                          message: 'Password must be less than 20 character',
                        },
                        validate: {
                          whitespace: (fieldValue: string) => {
                            return (
                              /^[ \t]+/.test(fieldValue) !== true ||
                              "Password can't be whitespace"
                            );
                          },
                        },
                      }}
                    />
                    <CustomTypography
                      variant='body2'
                      color='red'
                      align='justify'
                    >
                      {errors.password?.message?.toString()}
                    </CustomTypography>
                  </Grid>
                )}

                <Grid item sm={12}>
                  <CustomButton
                    className={classes.customButtonClass}
                    color='secondary'
                    type='submit'
                  >
                    {' '}
                    {user === 'Add' ? 'Add' : 'Update'}{' '}
                  </CustomButton>
                </Grid>
              </Grid>
            </form>
          </Box>
          <div onClick={handleCloseButton}>
            <CloseIcon
              fontSize='large'
              className={classes.closeIconClass}
              color='primary'
            />
          </div>
        </Box>
      </Modal>
    </Container>
  );
};

export default EditUser;
